import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

/** クラス名の結合。条件付きクラスを書きやすくするだけの薄いヘルパー */
export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

// --- ボタン -----------------------------------------------------------------

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost';
};

export function Button({ variant = 'primary', className, ...props }: ButtonProps) {
  return (
    <button
      {...props}
      className={cx(
        'w-full rounded-[10px] px-4 py-3.5 text-center text-[15px] font-bold transition',
        'active:scale-[0.99] disabled:cursor-not-allowed disabled:active:scale-100',
        variant === 'primary' && 'bg-felt text-bone-hi disabled:bg-off disabled:text-off-ink',
        variant === 'ghost' &&
          'border-[1.5px] border-felt bg-transparent text-felt disabled:border-off disabled:text-off-ink',
        className,
      )}
    />
  );
}

// --- カード -----------------------------------------------------------------

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...props}
      className={cx('rounded-xl border border-line bg-white p-3.5', className)}
    />
  );
}

/** 行を並べるカード。内側の区切り線は行側で引く */
export function ListCard({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...props}
      className={cx('rounded-xl border border-line bg-white px-3.5', className)}
    />
  );
}

// --- チップ -----------------------------------------------------------------

type ChipProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  selected?: boolean;
  tone?: 'default' | 'warn';
};

export function Chip({ selected, tone = 'default', className, ...props }: ChipProps) {
  return (
    <button
      type="button"
      {...props}
      className={cx(
        'rounded-full border px-3 py-1 text-xs transition',
        selected
          ? 'border-felt bg-felt font-medium text-bone-hi'
          : tone === 'warn'
            ? 'border-red bg-white text-red'
            : 'border-line bg-white text-ink-soft',
        className,
      )}
    />
  );
}

// --- アプリ内バー -----------------------------------------------------------

export function AppBar({
  title,
  subtitle,
  backTo,
  action,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  backTo?: string;
  action?: ReactNode;
}) {
  return (
    <header className="sticky top-0 z-10 bg-felt px-4 pb-3.5 pt-3 text-bone-hi">
      <div className="flex items-center gap-2.5">
        {backTo && (
          <Link
            to={backTo}
            aria-label="戻る"
            className="-ml-1 shrink-0 px-1 text-[22px] leading-none text-felt-sub"
          >
            ‹
          </Link>
        )}
        <div className="min-w-0">
          <div className="truncate text-[17px] font-bold leading-tight">{title}</div>
          {subtitle && <div className="mt-0.5 truncate text-xs text-felt-sub">{subtitle}</div>}
        </div>
        {action && <div className="ml-auto shrink-0 text-[13px] font-medium">{action}</div>}
      </div>
    </header>
  );
}

/** アプリ内バーの右側に置くテキストボタン */
export function BarAction({ className, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" {...props} className={cx('text-felt-accent', className)} />;
}

// --- 順位バッジ -------------------------------------------------------------

export function RankBadge({ rank, className }: { rank: number; className?: string }) {
  return (
    <span
      className={cx(
        'grid size-[22px] shrink-0 place-items-center rounded-md text-xs font-bold',
        rank === 1 ? 'bg-gold text-white' : 'bg-bone text-ink-soft',
        className,
      )}
    >
      {rank}
    </span>
  );
}

// --- 状態表示 ---------------------------------------------------------------

export function Label({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...props} className={cx('text-[11.5px] tracking-wide text-ink-faint', className)} />
  );
}

export function Spinner({ label = '読み込み中' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-ink-faint">
      <span
        aria-hidden
        className="size-4 animate-spin rounded-full border-2 border-line border-t-felt"
      />
      {label}
    </div>
  );
}

export function ErrorBox({
  title,
  message,
  action,
}: {
  title?: string;
  message: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-r-lg border-l-[3px] border-red bg-[#FBEDEB] px-3 py-2.5 text-[12.5px] leading-relaxed text-[#8C2A24]">
      {title && <div className="font-bold">{title}</div>}
      <div>{message}</div>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function InfoBox({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-r-lg border-l-[3px] border-gold bg-[#FBF3E2] px-3 py-2.5 text-[12.5px] leading-relaxed text-[#7A5A14]">
      {children}
    </div>
  );
}

/** データがまだ無いときの案内 */
export function EmptyState({ title, description }: { title: string; description?: string }) {
  return (
    <div className="rounded-xl border border-dashed border-line px-4 py-8 text-center">
      <div className="text-sm font-bold text-ink-soft">{title}</div>
      {description && <div className="mt-1 text-xs leading-relaxed text-ink-faint">{description}</div>}
    </div>
  );
}

// --- 入力 -------------------------------------------------------------------

export function TextField({
  label,
  hint,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  return (
    <label className="block">
      <span className="text-[11.5px] tracking-wide text-ink-faint">{label}</span>
      <input
        {...props}
        className={cx(
          'mt-1 w-full rounded-lg border border-line bg-white px-3 py-2.5 text-[15px]',
          'outline-none focus:border-felt focus:ring-1 focus:ring-felt',
          props.className,
        )}
      />
      {hint && <span className="mt-1 block text-[11px] text-ink-faint">{hint}</span>}
    </label>
  );
}

/** 画面の外枠。モバイル基準の幅で中央に置く */
export function Screen({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col bg-bone-hi shadow-sm">{children}</div>
  );
}

export function Body({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div {...props} className={cx('flex flex-1 flex-col gap-3.5 p-4', className)} />;
}

const FOOTER_LINKS = [
  { to: '/', label: '雀点トップ' },
  { to: '/terms', label: '利用規約' },
  { to: '/privacy', label: 'プライバシーポリシー' },
  { to: '/contact', label: 'お問い合わせ' },
];

/**
 * サイト共通のフッター。呼び出し側は何も渡さない。
 *
 * いま開いているページ自身へのリンクは落とす。トップに「雀点トップ」を、
 * /privacy に「プライバシーポリシー」を並べても行き先が無い。
 *
 * /g/ 配下では使わない。あそこは繰り返し使う「アプリ」の画面で、記録ボタンが
 * 下端に浮いていて重なる。noindex 済みなのでクロール用の導線も要らない。
 *
 * 地は牌の色。ページの地（bone-hi）との差は小さいので強い帯にはならず、トップの
 * ヒーローと同じ濃さの面がもう一枚下に来る見え方になる。面が変わるぶん区切り線は
 * 引かない。© に ink-soft を使っていないのは、この地では 4.28:1 で 4.5:1 を割るため。
 * felt-line なら 5.80:1 で通る。
 *
 * 上との間隔はここでは作らない。呼び出し側の Body の pb で空ける。色の付いた面に
 * mt を持たせると、面そのものが浮いて見える。
 */
export function SiteFooter() {
  const { pathname } = useLocation();

  return (
    <footer className="bg-bone px-4 pb-6 pt-5 text-[13px]">
      <div className="flex flex-wrap gap-4">
        {FOOTER_LINKS.filter((l) => l.to !== pathname).map((l) => (
          <Link key={l.to} to={l.to} className="font-medium text-felt">
            {l.to === '/' && (
              <span aria-hidden className="mr-0.5 text-felt-line">
                ‹
              </span>
            )}
            {l.label}
          </Link>
        ))}
      </div>
      <div className="mt-3.5 text-[12px] text-felt-line">© 雀点</div>
    </footer>
  );
}
